/* eslint-disable max-len */
import {
  Avatar,
  AvatarGroup,
  Box,
  Collapse,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { CircleAlert, DownloadIcon, FileText, GraduationCap, Hourglass, OctagonAlert, PencilRuler, UserRoundCheck } from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type KeyboardEvent,
  type MouseEvent
} from "react";
import { useTranslation } from "react-i18next";
import type { LearningActivity, ReferencedLearningActivity } from "types";
import { ActivityItem } from "./ActivityDotNavigation.types";
import {
  CardContent,
  CardWrapper,
  CtaContainer,
  MetadataRow,
  StatusBox,
  StepIndicator,
  SubtitleTypography,
  TitleRow,
  TitleTypography
} from "./CourseActivityCard.styles";
import type { CourseActivityCardProps } from "./CourseActivityCard.types";

import { isNotClassroom } from "./utils";
import { ButtonDigited } from "../../components/Button/Button.variants";
import { HiddenCTA } from "../../components/CarouselCardRestyle/HiddenCTA.component";
import { useDescendantClassrooms } from "../../components/DetailsEsploraRestyle/DescendantsClassrooms.context";
import { useActivityAndCourseSelection } from "../../components/DetailsEsploraRestyle/IndexActivitySelected.context";
import { LearningActivityModal } from "../../components/LearningActiviyModal/LearningActivityModal.component";
import { ctaDisabled, handleTeachersAndTutors } from "../../components/LearningActiviyModal/utils";
import { ENROLL_TYPE, LEVEL, STATUS_LO } from "../../consts";
import { useIsAlternativeContent } from "../../context/LearningObjectAlternativeContext";
import { useType } from "../../hooks";
import { useDownloadCertificate, useEditionsQuery } from "../../queries";
import { useAuthStore } from "../../stores";
import { formatDuration_h_m } from "../../utils/datetime";
import { clearPendingApprovalsFor, hasPendingApprovalAmong } from "../../utils/editionApproval";
import { downloadBlobFile } from "../../utils/general";
import { StatusBoxesContainer } from "../CarouselCardRestyle/CarouselCard.style";
import { StatusBox as EditionBadge } from "../Commons/StatusBox.component";
import { LearningObjectAlternativeFooter } from "../LearningObjectAlternativeSection/LearningObjectAlternativeFooter.component";
import { NumberLOTypography } from "../PathCourseAccordion/PathCourseAccordion.styles";
import {
  canSelfEnroll,
  getEditionJoinAvailability,
  getEditionMeetingUrl,
  isEnrolledLoStatus,
  oneUserEdition
} from "../TabsContent/Editions/utils";

function getInitials(completeName: string): string {
  return completeName
    .split(" ")
    .filter(Boolean)
    .map((word) => word[0]?.toUpperCase())
    .slice(0, 2)
    .join("");
}

function parsePercentage(value?: string): number {
  if (!value) return 0;
  const parsed = parseInt(value.replace("%", ""), 10);
  return isNaN(parsed) ? 0 : parsed;
}

export function CourseActivityCard({
  activity,
  alternativeSection,
  attributes,
  canAccessNow,
  certificate,
  courseId,
  disabled,
  duration,
  ecmRegistration,
  ecm_specialization,
  enrollType,
  enrollmentEnrollType,
  externalFileUrl,
  grandParentId,
  id,
  index,
  isActive = false,
  isAlternative = false,
  isBlinking = false,
  isEditorial,
  isFavourite,
  isLinkRelative,
  isMandatory,
  isRequiredInStructure,
  isStandAlone,
  isSurvey,
  isTest,
  learningObjectType,
  learningObjectTypology,
  link,
  madeAttempts,
  parentId,
  percentageOfCompletion,
  rootId,
  setOpenModalEcm,
  status,
  teachers,
  title,
  toolNumber,
  useful_links,
  isStandAloneActivity,
  onMouseEnter,
  handleActivityClick,
  initialActiveLoId
}: CourseActivityCardProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isAlternativeContent = useIsAlternativeContent();
  const isMobile = useMediaQuery(theme.breakpoints.down(500));
  const typeObjectLearning = useType(learningObjectType, learningObjectTypology);
  const [openEdition, setOpenEdition] = useState(false);
  const [_isDownloading, setIsDownloading] = useState(false);

  const userData = useAuthStore(state => state.userData);

  const { mutateAsync: mutateDownloadCertificate } = useDownloadCertificate();

  const { activityIdSelected, setActivityIdSelected, syncUrlToActivity } = useActivityAndCourseSelection();

  const showModalEcm = setOpenModalEcm && disabled && ecm_specialization && !ecmRegistration;

  const handleClick = useCallback(() => {
    if(activity?.id !== activityIdSelected) {
      setActivityIdSelected(activity?.id ?? undefined);
      syncUrlToActivity(activity as ActivityItem, isStandAloneActivity);
    }
  }, [activity, handleActivityClick, syncUrlToActivity]);

  useEffect(() => {
    initialActiveLoId && setActivityIdSelected(initialActiveLoId);
  }, [initialActiveLoId]);

  const handleDownloadCertificate = async () => {
    if (!certificate?.isEnable || !certificate?.id || !userData?.id_user) return;
    setIsDownloading(true);
    try {
      const blob = await mutateDownloadCertificate({
        certificateId: certificate.id,
        learnerId: userData.id_user as number,
        learningObjectId: id
      });
      downloadBlobFile({
        blobFile: blob,
        fileName: `issued_certificate_lo_${id}_learner_${userData.id_user}.pdf`
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const isCompleted = status === "C";
  
  const resourcesCount = (toolNumber ?? 0) + (useful_links?.length ?? 0);
  const isEffectivelyMandatory = isRequiredInStructure ?? isMandatory;
  //const isTestCompleted = (isTest || isSurvey) && isCompleted;
  const percentage = parsePercentage(percentageOfCompletion);

  const typologyLabel = isTest ? t("test") : isSurvey ? t("survey") : typeObjectLearning;

  // Aria label for accessibility (same logic as CarouselCardRestyle)
  let ariaLabel = title;
  if (typologyLabel) {
    ariaLabel = `${title}. ${t("typology_carousel")}:${typologyLabel}`;
  }
  if (percentage > 0) {
    ariaLabel = `${ariaLabel}. ${t("completion_percentage")} ${percentageOfCompletion}`;
  }
  if (duration) {
    ariaLabel = `${ariaLabel}. ${t("duration")}:${duration}`;
  }
  if (isCompleted) {
    ariaLabel = `${ariaLabel}. ${t("status_lo")}:${t("completed")}`;
  } else if (isEffectivelyMandatory) {
    ariaLabel = `${ariaLabel}. ${t("status_lo")}:${t("mandatory")}`;
  }

  useEffect(() => {
    isActive && syncUrlToActivity(activity as ActivityItem);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isClassroom = !isNotClassroom(learningObjectTypology);
  // Le editions da useEditionsQuery servono per il pulsante "partecipa" (join) dell'aula.
  const { data: editions, isLoading: areEditionsLoading } = useEditionsQuery({ classroomId: activity?.id.toString() ?? "", enabled: isClassroom });
  // Include le edizioni concluse ("C"): a fruizione completata il CTA "Partecipa" resta visibile
  // ma disabilitato ("la data è passata"), non sparisce.
  const enrolledEdition = editions?.find((edition) => isEnrolledLoStatus(edition.status) && !edition.isAbsent);

  // Stato di iscrizione dell'utente dai descendants-classrooms (unica chiamata per pagina):
  // lo status delle edizioni è globale, quindi non dice nulla sull'utente.
  const {
    getDescendantClassroom,
    isUnavailable: descendantsUnavailable,
    approvalGranted: parentApprovalGranted
  } = useDescendantClassrooms();
  const descendant = getDescendantClassroom(activity?.id);
  const descendantEditions = descendant?.editions ?? [];
  const enrollStatus = descendant?.enrollStatus;
  // `enrollStatus` ed `editionId` a livello di record descrivono l'assegnazione dell'LO, non
  // l'iscrizione a una specifica edizione, e il BE non li tiene allineati a quest'ultima
  // (riscontrato in produzione: `editionId` resta null e `enrollStatus` assume valori come "PA"
  // anche con un'edizione realmente assegnata all'utente). Il segnale affidabile è lo status
  // per-edizione dentro `editions[]` — la stessa fonte già usata per `enrolledEdition` — che
  // torna "N" non appena l'utente si disiscrive.
  const userDescendantEnrolledEdition = descendantEditions.find((edition) => isEnrolledLoStatus(edition.status));
  const hasUserEdition = descendant?.editionId != null || !!userDescendantEnrolledEdition;
  const isEnrolledClassroom = hasUserEdition;
  const isCompletedClassroom = enrollStatus === "C";
  // `selfEnrollment` è il flag della singola aula: da solo non basta a dire che l'iscrizione è
  // top-down. `enrollType` porta la modalità di iscrizione ("TOP"/"TOP_DOWN"/"TOP_MANDATORY" solo
  // se l'utente non può iscriversi da sé; "PRE_AUTO"/"BOTTOM" per le auto-iscrizioni).
  const canSelfEnrollClassroom = canSelfEnroll(descendant?.selfEnrollment ?? false, descendant?.enrollType);
  // Corso/percorso ad auto-iscrizione con approvazione la cui richiesta non è ancora stata concessa
  // (indifferentemente: mai inviata oppure in attesa del responsabile — il BE restituisce lo stesso
  // payload nei due casi). Finché dura, l'iscrizione ai figli non è nemmeno ipotizzabile: la card
  // non espone niente, né badge né edizioni né staff. Il segnale è il prefisso dell'enroll type
  // dell'iscrizione, che diventa "BOTTOM" appena la richiesta viene approvata.
  // Il segnale primario è lo stato di iscrizione del padre, letto dal contesto della pagina di
  // dettaglio; il prefisso dell'enroll type dell'iscrizione resta come fallback per le card
  // renderizzate fuori da quel contesto, dove il LO padre non è disponibile.
  const parentEnrollType = descendant?.enrollType ?? enrollmentEnrollType;
  const isParentApprovalPending = !parentApprovalGranted || parentEnrollType === "PRE_BOTTOM";
  // Richiesta di iscrizione inviata sulla singola aula e in attesa del responsabile (aula con flag
  // di autoiscrizione = no sotto un padre già approvato).
  // Il BE non espone ancora lo stato "W" in questo scenario: finché non lo farà il segnale è la
  // richiesta registrata lato client al momento dell'invio (vedi utils/editionApproval). Quella
  // traccia però vale solo finché il BE non si pronuncia: appena l'utente risulta iscritto o
  // l'aula si rivela di un regime che l'approvazione non la prevede, il dato remoto ha ragione.
  const classroomNeedsApproval = parentEnrollType === ENROLL_TYPE.REQUESTED_AUTO_ENROLL
    || parentEnrollType === "PRE_BOTTOM";
  const hasStoredApprovalRequest = classroomNeedsApproval
    && !isEnrolledClassroom
    && !isCompletedClassroom
    && hasPendingApprovalAmong(
      descendantEditions.map((edition) => edition.editionId),
      userData?.id_user as number
    );
  const isPendingClassroomApproval = isClassroom
    && !isParentApprovalPending
    && (enrollStatus === STATUS_LO.W
      || descendantEditions.some((edition) => edition.status === STATUS_LO.W)
      || hasStoredApprovalRequest);

  // Approvazione arrivata (o iscrizione comunque perfezionata): la richiesta locale ha esaurito
  // il suo scopo e va rimossa, altrimenti il badge tornerebbe a ogni reload.
  useEffect(() => {
    if (!isClassroom || !userData?.id_user) return;
    if (!isEnrolledClassroom && !isCompletedClassroom) return;
    clearPendingApprovalsFor(
      descendantEditions.map((edition) => edition.editionId),
      userData.id_user as number
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClassroom, isEnrolledClassroom, isCompletedClassroom, userData?.id_user, descendant?.editionId]);
  // Finché l'approvazione manca nessuna CTA del figlio è azionabile: né l'accesso al contenuto,
  // né il dettaglio dell'aula (da cui si arriverebbe all'iscrizione a un'edizione), né "Partecipa".
  const lockedByApproval = isParentApprovalPending || isPendingClassroomApproval;
  // "Disponibili" = edizioni con posti liberi.
  const availableEditionsCount = descendantEditions
    .filter((edition) => (edition.totalPlaces - edition.occupiedPlaces) > 0).length;
  // Edizione dell'utente: `editionId` da descendants-classrooms è il dato per-utente quando c'è;
  // altrimenti si ricade sull'edizione trovata via status in `editions[]`, poi sul catalogo
  // (card fuori dal contesto di dettaglio).
  const userEditionId = descendant?.editionId ?? userDescendantEnrolledEdition?.editionId ?? enrolledEdition?.id;
  const userEdition = userEditionId != null
    ? editions?.find((edition) => edition.id === userEditionId)
    : undefined;
  // Assente: assenza registrata sull'edizione dell'utente. L'assenza non annulla l'iscrizione —
  // `editionId` ed `enrollStatus` restano valorizzati — quindi va letta sull'edizione, altrimenti
  // la card continuerebbe a dire "Iscrizione effettuata" a chi risulta assente.
  // Il campo lo valorizza `editionsWithSessions` (la stessa chiamata che serve "Partecipa");
  // descendants-classrooms non lo espone, resta come fallback se un giorno lo farà.
  const userDescendantEdition = descendant?.editionId != null
    ? descendantEditions.find((edition) => edition.editionId === descendant.editionId)
    : userDescendantEnrolledEdition;
  const isAbsentOnUserEdition = userEdition?.isAbsent
    ?? userDescendantEdition?.isAbsent
    ?? descendantEditions.some((edition) => edition.isAbsent);
  const wasAbsent = isClassroom && !!descendant && !isCompletedClassroom
    && !isParentApprovalPending
    && isAbsentOnUserEdition;
  // Iscrizione ancora "viva": l'assenza la chiude ai fini di badge e CTA, ma non di docenti e
  // risorse, che restano accessibili a chi era iscritto all'edizione (vedi `hideStaffAndResources`).
  const hasActiveEnrollment = isEnrolledClassroom && !wasAbsent;
  // Finché `editionsWithSessions` è in volo non sappiamo se l'utente è assente: mostrare
  // "Iscrizione effettuata" adesso significherebbe farlo lampeggiare in "Assente" subito dopo.
  // Si usa `isLoading` e non `isPending`: copre solo la fetch davvero in corso, quindi se la query
  // è disabilitata o fallisce si torna al comportamento precedente invece di nascondere il badge.
  const isAbsenceUnknown = isEnrolledClassroom && areEditionsLoading;

  // Se il dato per-utente non arriverà (chiamata fallita o non eseguita) si ricade sul criterio
  // basato sulle edizioni: meno preciso, ma evita di nascondere risorse e docenti a chi è iscritto.
  // Durante il caricamento si resta sul comportamento prudente (nascosti finché non si sa).
  const fallbackEnrolled = editions && editions.length > 0
    ? oneUserEdition(editions)
    : isEnrolledLoStatus(status);
  const hideStaffAndResources = isClassroom
    && (isParentApprovalPending
      || isPendingClassroomApproval
      || (descendantsUnavailable ? !fallbackEnrolled : !isEnrolledClassroom));

  const editionStaff = useMemo(() => {
    const collect = (key: "teachers" | "tutors") => Array.from(
      new Map(
        (userEdition?.sessions ?? [])
          .flatMap((session) => session[key] ?? [])
          .map((person) => [person.id, person])
      ).values()
    );
    return handleTeachersAndTutors(collect("teachers"), collect("tutors"), t);
  }, [userEdition, t]);

  const displayedTeachers = isClassroom ? editionStaff : (teachers ?? []);

  // Badge di stato (solo per aule con dati descendants disponibili):
  // - iscritto -> "Iscrizione effettuata"; assente -> "Assente";
  // - non iscritto + autoiscrizione -> "Necessaria iscrizione" + N edizioni;
  // - non iscritto + puro top-down -> "Presto verrai iscritto".
  // Con l'approvazione del padre ancora pendente nessuno di questi stati è mostrabile.
  const showEnrolledBadge = isClassroom && !!descendant && !isCompletedClassroom && hasActiveEnrollment
    && !isAbsenceUnknown && !isParentApprovalPending && !isPendingClassroomApproval;
  const needsSelfEnrollment = isClassroom && !!descendant && !isCompletedClassroom && !hasActiveEnrollment && !wasAbsent && canSelfEnrollClassroom
    && !isParentApprovalPending && !isPendingClassroomApproval;
  const isWaitingTopDownEnrollment = isClassroom && !!descendant && !isCompletedClassroom && !hasActiveEnrollment && !wasAbsent && !canSelfEnrollClassroom
    && !isParentApprovalPending && !isPendingClassroomApproval;
  // CTA "Vedi le edizioni": solo se auto-iscrivibile con edizioni disponibili. Vale anche per chi
  // risulta assente: è il modo per iscriversi a un'altra edizione.
  const showEditionsCta = canSelfEnrollClassroom && !hasActiveEnrollment && !isCompletedClassroom && availableEditionsCount > 0
    && !isAbsenceUnknown && !isParentApprovalPending && !isPendingClassroomApproval;
  const waitingLabelKey = learningObjectTypology === LEVEL.DA_PHYSICAL_CLASS ? "physical_class_pending" : "virtual_class_pending";
  // Conteggio edizioni accanto al badge: prima dell'iscrizione sono quelle a cui ci si può
  // iscrivere, dopo sono quelle a cui l'utente è iscritto (il BE, a iscrizione avvenuta,
  // restituisce solo le sue).
  const enrolledEditionsCount = descendantEditions
    .filter((edition) => isEnrolledLoStatus(edition.status)).length;
  const editionsCount = showEnrolledBadge ? enrolledEditionsCount : availableEditionsCount;
  const showEditionsCount = (needsSelfEnrollment || showEnrolledBadge) && editionsCount > 0;

  const isVirtualClassroom = learningObjectTypology === LEVEL.DA_VIRTUAL_CLASS;
  const joinAvailability = isClassroom && enrolledEdition
    ? getEditionJoinAvailability(enrolledEdition, isVirtualClassroom)
    : "not_yet";
  const joinUrl = enrolledEdition ? getEditionMeetingUrl(enrolledEdition) : null;
  // Anche l'aula fisica espone "Partecipa", con la sua finestra di 2h: la mostriamo però solo se
  // l'edizione porta un link, altrimenti sarebbe un pulsante senza destinazione.
  const showJoinButton = isClassroom && !!enrolledEdition && (isVirtualClassroom || !!joinUrl);
  const canJoinClassroom = joinAvailability === "available" && !!joinUrl;
  const joinTooltipKey = joinAvailability === "ended" ? "join_no_longer_available" : "join_not_available_yet";
  const handleJoinClassroom = () => {
    if (!joinUrl) return;
    window.open(joinUrl, "_blank", "noreferrer");
  };

  return (
    <CardWrapper
      role="region"
      className={ "course-activity-card" }
      data-testid={ isAlternative ? "alternative-activity-card" : undefined }
      tabIndex={ 0 }
      aria-label={ ariaLabel }
      $isActive={  activityIdSelected === activity?.id }
      $isComplete={ isCompleted }
      sx={ {
        ...(isAlternative && {
          backgroundColor: theme.palette.background?.default ?? theme.customColors.backgroundPrimary
        }),
        ...(isBlinking ? {
          "@keyframes heroCtaBlink": {
            "0%, 100%": { boxShadow: "inset 0 0 0 0 transparent" },
            "50%": { boxShadow: `inset 0 0 0 3px ${theme.customColors.borderAccent}` }
          },
          animation: "heroCtaBlink 0.6s ease-in-out 3"
        } : {})
      } }
      onClick={ (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        handleClick();
        onMouseEnter && onMouseEnter(activity?.id);
        if (showModalEcm) {
          setOpenModalEcm(true);
        }
      } }
      onKeyDown={ (e: KeyboardEvent<HTMLElement>) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();

          if (showModalEcm) {
            setOpenModalEcm?.(true);
          }
        }
      } }
    >
      { /* Step indicator with circular progress */ }
      { /* Card body */ }
      <CardContent
        isStart={ true }
        id={ `card-content-${activity?.id}` }
        item
        xs
        width={ "100%" }
        gap={ "1rem" }
      >
        <Stack className="info-row"
          flexDirection={ "row" }
          alignItems={ "center" }
          gap={ "1rem" }
          width={ "100%" }
          sx={ {
            cursor: "pointer"
          } }
        >
          <StepIndicator>
            <Box position="relative" display="inline-flex">
              <Box alignSelf="center" display="flex" flexShrink={ 0 }>
                <NumberLOTypography
                  sx={ {
                    minWidth: 24,
                    textAlign: "center"
                  } }>
                  { index }
                </NumberLOTypography>
              </Box>
            </Box>
          </StepIndicator>
          <Stack
            flex={ 1 }
            minWidth={ 0 }
          >
            <TitleRow>
              <TitleTypography
                color={ theme.customColors.exploreCardText }
              >
                { title }
              </TitleTypography>

            </TitleRow>

            <SubtitleTypography
              color={ theme.customColors.exploreCardText }
            >
              { typologyLabel }
              { duration ? ` | ${formatDuration_h_m(duration)}` : "" }
            </SubtitleTypography>
          </Stack>

        </Stack>
      </CardContent>

      <StatusBox sx={ { alignItems: "center", paddingY: theme.spacing(1) } }>
        { isClassroom && (
          <EditionBadge
            id={ `classroom-type-${id}` }
            ariaLabel={ typeObjectLearning }
            backgroundColor={ theme.customColors.backgroundSecondary }
            border={ `1px solid ${theme.customColors.textTag}` }
            color={ theme.customColors.textTag }
            icon={ <PencilRuler size={ "0.875rem" } color={ theme.customColors.textTag } /> }
            label=""
          />
        ) }
        { showEnrolledBadge && (
          <EditionBadge
            id={ `edition-enrolled-${id}` }
            backgroundColor={ theme.customColors.backgroundSecondary }
            border={ `1px solid ${theme.customColors.textTag}` }
            color={ theme.customColors.textTag }
            icon={ <UserRoundCheck size={ "0.875rem" } color={ theme.customColors.textTag } /> }
            label={ t("enrollment-success") }
          />
        ) }
        { isWaitingTopDownEnrollment && (
          <EditionBadge
            id={ `edition-waiting-${id}` }
            backgroundColor={ theme.customColors.backgroundSecondaryAlternative }
            border={ `1px solid ${theme.customColors.backgroundAccent}` }
            color={ theme.customColors.textTag }
            icon={ <PencilRuler size={ "0.875rem" } color={ theme.customColors.textTag } /> }
            label={ t(waitingLabelKey) }
          />
        ) }
        { isPendingClassroomApproval && (
          <EditionBadge
            id={ `edition-approval-required-${id}` }
            backgroundColor={ theme.customColors.backgroundSecondaryAlternative }
            border={ `1px solid ${theme.customColors.backgroundAccent}` }
            color={ theme.customColors.textTag }
            icon={ <Hourglass size={ "0.875rem" } color={ theme.customColors.textTag } /> }
            label={ t("enrollment_approval_required") }
          />
        ) }
        { wasAbsent && (
          <EditionBadge
            id={ `edition-absent-${id}` }
            backgroundColor={ theme.customColors.backgroundSecondary }
            border={ `1px solid ${theme.customColors.textTag}` }
            color={ theme.customColors.textTag }
            icon={ <OctagonAlert size={ "0.875rem" } color={ theme.customColors.textTag } /> }
            label={ t("absent") }
          />
        ) }
        { needsSelfEnrollment && (
          <EditionBadge
            id={ `edition-enrollment-required-${id}` }
            backgroundColor={ theme.customColors.backgroundSecondary }
            border={ `1px solid ${theme.customColors.textTag}` }
            color={ theme.customColors.textTag }
            icon={ <CircleAlert size={ "0.875rem" } color={ theme.customColors.textTag } /> }
            label={ t("enrollment_required") }
          />
        ) }
        <StatusBoxesContainer
          attributes={ attributes }
          endDate={ undefined }
          expirationDate={ undefined }
          hideMandatoryAndAttributes
          id={ id }
          isCompleted={ isCompleted }
          inProgress={ status === "P" }
          isExpired={ false }
          isMandatory={ isEffectivelyMandatory }
          learningObjectTypology={ learningObjectTypology }
          showMandatory={ !isAlternativeContent && isRequiredInStructure === true }
          startDate={ undefined }
        />
      </StatusBox>
      <Box
        className="card-bottom-box"
        display="flex"
        flexDirection="column"
        alignItems="flex-end"
        flexShrink={ 0 }
        justifyContent="space-between"
        alignSelf="stretch"
        sx={ {
          mt: "auto",
          width: "100%",
          paddingLeft: "40px"
        } }
      >
        <Collapse
          in={ activityIdSelected === activity?.id }
          timeout={ 150 }
          easing="ease-in-out"
          sx={ {
            width: "100%",
            "& .MuiCollapse-wrapper": {
              width: "100%"
            },
            "& .MuiCollapse-wrapperInner": {
              alignItems: "center",
              display: "flex",
              flexWrap: "wrap",
              gap: "1rem",
              justifyContent: "space-between",
              paddingTop: theme.spacing(2),
              transition: "all .15s ease-in-out",
              width: "100%",
              [theme.breakpoints.up("sm")]: {
                flexWrap: "nowrap"
              }
            }
          } }
        >
          <MetadataRow
            id={ `meta-data-activity-${id}` }
          >
            { showEditionsCount && (
              <Stack direction="row" alignItems="center" gap={ 0.5 }>
                <PencilRuler
                  aria-hidden="true"
                  size={ "1rem" }
                  color={ theme.customColors.exploreCardText }
                />
                <Typography
                  fontSize={ "0.75rem" }
                  variant="body2"
                  color={ theme.customColors.exploreCardText }
                >
                  { editionsCount } { " " } { t("editions") }
                </Typography>
              </Stack>
            ) }
            { resourcesCount > 0 && !hideStaffAndResources && (
              <Stack direction="row" alignItems="center" gap={ 0.5 }>
                <FileText
                  aria-hidden="true"
                  size={ "1rem" }
                  color={ theme.customColors.exploreCardText }
                />
                <Typography
                  fontSize={ "0.75rem" }
                  variant="body2"
                  color={ theme.customColors.exploreCardText }
                >
                  { t("resource", { count: resourcesCount }) }
                </Typography>
              </Stack>
            ) }

            { displayedTeachers.length > 0 && !hideStaffAndResources && (
              <Stack direction="row" alignItems="center" gap={ 1 }>
                <GraduationCap
                  aria-hidden="true"
                  size={ "1rem" }
                  color={ theme.customColors.exploreCardText }
                />
                <Typography
                  variant="body2"
                  fontSize={ "0.75rem" }
                  color={ theme.customColors.exploreCardText }>
                  { t("teachers") }
                </Typography>
                <AvatarGroup
                  max={ 4 }
                  sx={ {
                    "& .MuiAvatar-root": {
                      border: `2px solid ${theme.customColors.backgroundPrimary}`,
                      fontSize: "0.5rem",
                      height: "1.25rem",
                      lineHeight: "0.75rem",
                      width: "1.25rem"
                    }
                  } }
                >
                  { displayedTeachers.map((teacher) => (
                    <Avatar
                      key={ teacher.id }
                      alt={ teacher.completeName }
                      src={ teacher.image || undefined }
                      sx={ {
                        bgcolor: theme.customColors.backgroundPrimaryCta
                      } }
                    >
                      { getInitials(teacher.completeName) }
                    </Avatar>
                  )) }
                </AvatarGroup>
              </Stack>
            ) }

            <Stack
              flexDirection={ "row" }
              gap="0.75rem"
            >
              { certificate && (
                <DownloadIcon
                  size={ "1.25rem" }
                  color={ certificate.isEnable
                    ? theme.customColors.exploreCardText
                    : theme.customColors.border }
                  style={ {
                    cursor: certificate.isEnable ? "pointer" : "not-allowed"
                  } }
                  aria-label={ t("download") }
                  role="button"
                  tabIndex={ 0 }
                  onClick={ (e) => {
                    e.stopPropagation();
                    handleDownloadCertificate();
                  } }
                />
              ) }
            </Stack>
          </MetadataRow>
          <CtaContainer sx={ {
            "& .card-cta": {
              gap: 0
            },
            "& .card-cta > .MuiStack-root:empty": {
              display: "none"
            },
            "& .collapse_cta": {
              gap: 0,
              height: "auto",
              opacity: 1
            },
            alignItems: "flex-end",
            flexDirection: "column",
            gap: 0
          } }>
            <Stack
              direction="row"
              spacing="0.5rem"
              gap={ isMobile ? "0.5rem" : "unset" }
              flexWrap={ "wrap" }
            >
              { isNotClassroom(learningObjectTypology) ? (
                <>
                  <HiddenCTA
                    attributes={ attributes }
                    canAccessNow={ canAccessNow }
                    courseId={ courseId }
                    disabled={ ctaDisabled(activity as LearningActivity) || disabled || lockedByApproval }
                    ecm_specialization={ ecm_specialization ? ecm_specialization : false }
                    ecmRegistration={ ecmRegistration ? ecmRegistration : false }
                    enrollType={ enrollType }
                    externalFileUrl={ externalFileUrl }
                    grandParentId={ grandParentId ? grandParentId : undefined }
                    id={ id }
                    isCard={ false }
                    isEditorial={ false }
                    isLinkRelative={ isLinkRelative }
                    isStandAlone={ isStandAlone }
                    isSurvey={ isSurvey }
                    isHeroBanner={ false }
                    isTest={ isTest }
                    learningObjectType={ learningObjectType }
                    learningObjectTypology={ learningObjectTypology }
                    link={ isEditorial && isLinkRelative && externalFileUrl ? externalFileUrl : link }
                    //TODO isfavourite lasciamo a false?
                    isFavourite={ !!isFavourite }
                    madeAttempts={ madeAttempts }
                    parentId={ parentId ? parentId : undefined }
                    percentageOfCompletion={ percentageOfCompletion }
                    rootId={ rootId }
                    showDetailsButton={ false }
                    status={ status || "N" }
                  />
                  <LearningActivityModal open={ openEdition } setOpen={ setOpenEdition } buttonType={ "secondary" } buttonDisabled={ Boolean(disabled) || lockedByApproval } activity={ activity as ReferencedLearningActivity }></LearningActivityModal>
                </>
              ) : (
                <>
                  { showJoinButton && (
                    <Tooltip title={ canJoinClassroom ? "" : t(joinTooltipKey) }>
                      <span style={ { display: "inline-flex" } }>
                        <ButtonDigited
                          id={ `activity-join-button-${id}` }
                          data-testid="activity-join-button"
                          aria-label={ t("join") }
                          typology="primary"
                          disabled={ !canJoinClassroom || Boolean(disabled) || lockedByApproval }
                          onClick={ (e) => {
                            e.stopPropagation();
                            handleJoinClassroom();
                          } }
                        >
                          { t("join") }
                        </ButtonDigited>
                      </span>
                    </Tooltip>
                  ) }
                  { /* Con l'assenza registrata "Vedi le edizioni" serve a iscriversi a un'altra
                       edizione, ma l'utente deve poter tornare anche al dettaglio di quella a cui
                       era iscritto: i due CTA convivono. Aprono la stessa modale, che espone le
                       edizioni come prima tab. */ }
                  { showEditionsCta && wasAbsent && (
                    <ButtonDigited
                      id={ `activity-editions-button-${id}` }
                      data-testid="activity-editions-button"
                      aria-label={ t("editions_show") }
                      typology="primary"
                      disabled={ Boolean(disabled) || lockedByApproval }
                      onClick={ (e) => {
                        e.stopPropagation();
                        setOpenEdition(true);
                      } }
                    >
                      { t("editions_show") }
                    </ButtonDigited>
                  ) }
                  <LearningActivityModal
                    open={ openEdition }
                    setOpen={ setOpenEdition }
                    buttonType={ showEditionsCta && !wasAbsent ? "primary" : "secondary" }
                    buttonLabel={ showEditionsCta && !wasAbsent ? t("editions_show") : t("details") }
                    buttonDisabled={ Boolean(disabled) || lockedByApproval }
                    activity={ activity as ReferencedLearningActivity }
                    initialEditions={ editions }
                  />
                </>
              ) }
            </Stack>
          </CtaContainer>
        </Collapse>
      </Box>

      { alternativeSection && (
        <LearningObjectAlternativeFooter
          canAccessNow={ canAccessNow }
          disabled={ disabled || lockedByApproval }
          ecmRegistration={ ecmRegistration }
          ecm_specialization={ ecm_specialization }
          rootId={ rootId }
          section={ alternativeSection }
          setOpenModalEcm={ setOpenModalEcm }
          sx={ { marginTop: "0.75rem" } }
        />
      ) }
    </CardWrapper>
  );
}
